
import { useState } from 'react';

interface FilterProps {
  onFilter: (filters: any) => void;
}

export default function PortalFilters({ onFilter }: FilterProps) {
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    location: 'all',
    mrrRange: [0, 100000],
    niches: [] as string[],
    featured: false
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilter(newFilters);
  };

  const handleNicheToggle = (niche: string) => {
    const newNiches = filters.niches.includes(niche)
      ? filters.niches.filter(n => n !== niche)
      : [...filters.niches, niche];
    handleFilterChange('niches', newNiches);
  };

  const clearFilters = () => {
    const defaultFilters = {
      search: '',
      type: 'all',
      location: 'all',
      mrrRange: [0, 100000],
      niches: [],
      featured: false
    };
    setFilters(defaultFilters);
    onFilter(defaultFilters);
  };

  const popularNiches = [
    'E-commerce', 'Performance Marketing', 'SEO', 'Content Marketing',
    'Social Media', 'Email Marketing', 'Video Production', 'Brand Design',
    'Web Development', 'Paid Ads', 'Influencer Marketing', 'B2B SaaS'
  ];

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 mb-12">
      {/* Main Search */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          <input
            type="text"
            placeholder="Search by description, niche, or location..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="w-full bg-black/50 border border-white/20 rounded-xl pl-12 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-white/40 transition-colors"
          />
        </div>
        
        <div className="flex gap-3">
          <select
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            className="bg-black/50 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-white/40 transition-colors pr-8"
          >
            <option value="all">All Types</option>
            <option value="agency">Agency</option>
            <option value="saas">SaaS</option>
            <option value="ecommerce">E-commerce</option>
          </select>

          <select
            value={filters.location}
            onChange={(e) => handleFilterChange('location', e.target.value)}
            className="bg-black/50 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-white/40 transition-colors pr-8"
          >
            <option value="all">All Locations</option>
            <option value="CA">California</option>
            <option value="TX">Texas</option>
            <option value="FL">Florida</option>
            <option value="CO">Colorado</option>
            <option value="OR">Oregon</option>
          </select>

          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl px-4 py-3 text-white transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className={`ri-filter-3-line mr-2 ${showAdvanced ? 'text-blue-400' : ''}`}></i>
            Filters
          </button>
        </div>
      </div>

      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="border-t border-white/10 pt-6 space-y-6">
          {/* MRR Range */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Monthly Recurring Revenue (MRR)
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="range"
                min="0"
                max="100000"
                step="5000"
                value={filters.mrrRange[1]}
                onChange={(e) => handleFilterChange('mrrRange', [0, parseInt(e.target.value)])}
                className="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
              />
              <div className="text-sm text-gray-400 whitespace-nowrap">
                Up to ${filters.mrrRange[1].toLocaleString()}/mo
              </div>
            </div>
          </div>

          {/* Niches */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Specializations
            </label>
            <div className="flex flex-wrap gap-2">
              {popularNiches.map((niche) => (
                <button
                  key={niche}
                  onClick={() => handleNicheToggle(niche)}
                  className={`px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    filters.niches.includes(niche)
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  {niche}
                </button>
              ))}
            </div>
          </div>

          {/* Featured Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Featured Listings Only
              </label>
              <p className="text-xs text-gray-500">Show only premium verified listings</p>
            </div>
            <button
              onClick={() => handleFilterChange('featured', !filters.featured)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                filters.featured ? 'bg-blue-500' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  filters.featured ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Clear Filters */}
          <div className="flex justify-end">
            <button
              onClick={clearFilters}
              className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line mr-2"></i>
              Clear all filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
